import React, { useState, useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useKeycloak } from '../config/KeycloakContext';
import { useUserContext } from '../config/UserContext';
import { useNavigate } from 'react-router-dom';

interface SessionTimeoutModalProps {
  timeoutMs?: number;
  promptBeforeIdleMs?: number;
  isKioskMode?: boolean;
}

export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  timeoutMs = 10 * 60 * 1000, // 10 minutes
  promptBeforeIdleMs = 2 * 60 * 1000, // 2 minutes warning
  isKioskMode = false,
}) => {
  const [open, setOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const { logout, initialized } = useKeycloak();
  const { setDocument, setEntities, setFreeze, setCounty, setState } = useUserContext();
  const navigate = useNavigate();

  const onIdle = () => {
    setOpen(false);
    
    // Clear user context to clear PII off screen
    setDocument(null);
    setEntities([]);
    setFreeze(null);
    
    if (isKioskMode) {
      // Kiosk mode: just navigate to search, keep SSO session active
      navigate('/search');
    } else {
      // Standard mode: complete logout with redirect back to current URL
      const currentUrl = window.location.href;
      // Because logout() inside KeycloakContext.tsx triggers a native keycloak logout,
      // it natively handles redirects if configured. However, our logout() implementation
      // in KeycloakContext is just `keycloak?.logout()`.
      // We can directly call window.location to redirect after logout, or pass the redirectUri
      // if we had direct access to keycloak.
      
      // We will dispatch a custom event or let KeycloakContext handle it.
      // Wait, let's just trigger a click or call Keycloak context logout.
      logout();
    }
  };

  const onPrompt = () => {
    setOpen(true);
  };

  const onActive = () => {
    setOpen(false);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout: timeoutMs,
    promptBeforeIdle: promptBeforeIdleMs,
    throttle: 500,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (open) {
        setRemaining(Math.ceil(getRemainingTime() / 1000));
      }
    }, 500);

    return () => clearInterval(interval);
  }, [open, getRemainingTime]);

  const handleStayLoggedIn = () => {
    setOpen(false);
    activate();
  };

  if (!initialized) return null;
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-4 font-plus-jakarta">Session Expiring Soon</h2>
        <p className="text-gray-600 mb-6 font-plus-jakarta">
          Your session will expire in {Math.floor(remaining / 60)}:{(remaining % 60).toString().padStart(2, '0')} due to inactivity.
          <br /><br />
          Do you want to stay signed in?
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="w-full bg-[#0B4BB6] text-white font-bold py-2 px-4 rounded transition-colors hover:bg-[#23235a]"
          >
            Stay Signed In
          </button>
          <button
            onClick={onIdle}
            className="w-full border border-gray-300 text-gray-700 font-bold py-2 px-4 rounded transition-colors hover:bg-gray-100"
          >
            Log Out Now
          </button>
        </div>
      </div>
    </div>
  );
};
